import classNames from 'classnames';
import { FC, useEffect, useState } from 'react';
import { Size, useWindowSize } from '../../hook/useWindowSize';
import BusinessButton from '../business-button/business-button';
import { COLOR, POSITION } from '../business-button/business-button.constants';
import styles from './business-hub-image-block.module.scss';

export enum IMAGE_POSITION {
    Left = 'Left',
    Right = 'Right',
    Top = 'Top',
    TopRight = 'TopRight',
    Bottom = 'Bottom',
    BottomRight = 'BottomRight',
}

export enum IMAGE_POSITION_MOBILE {
    TopMobile = 'Top',
    BottomMobile = 'Bottom',
}

export enum THEME_CONTENT {
    default = 'default',
    gray = 'gray',
}

export type ImageBlockProps = {
    image?: {
        source?: string;
        sourceMobile?: string;
        positionImage: IMAGE_POSITION;
        positionImageMobile: IMAGE_POSITION_MOBILE;
        maxWidth?: number;
        maxWidthMobile?: number;
    };
    backgroundLoop?: string;
    bottomBackgroundLoop?: number;
    positionImageLoop?: 'top' | 'bottom';
    heightContainer?: number;
    paddingTopContainer?: number;
    paddingBottomContainer?: number;
    paddingTopContainerMobile?: number;
    paddingBottomContainerMobile?: number;

    text: {
        title?: string;
        maxWidthTitleMobile: number;
        description?: string;
        maxWidthDescription?: number;
        ctaText?: string;
        ctaLink?: string;
        fillColor?: string;
        backgroundButton?: COLOR;
    };

    backgroundImage?: {
        image?: string;
        imageMobile?: string;
        zIndex?: number;
    };

    backgroundColor?: string;
    backgroundLinearGradient?: string;
    backgroundContent?: THEME_CONTENT;
};

const BusinessHubImageBlock: FC<ImageBlockProps> = (props) => {
    const {
        image,
        backgroundColor,
        text,
        backgroundImage,
        backgroundLinearGradient,
        heightContainer,
        paddingTopContainer,
        paddingBottomContainer,
        paddingBottomContainerMobile,
        paddingTopContainerMobile,
        backgroundContent,
        backgroundLoop,
        bottomBackgroundLoop,
        positionImageLoop,
    } = props;
    const [isMobile, setIsMobile] = useState<boolean>(false);

    const windowSize: Size = useWindowSize();

    const windowWidth: number = windowSize.width || 0;

    useEffect(() => {
        if (windowWidth <= 998) {
            setIsMobile(true);
        } else {
            setIsMobile(false);
        }
    }, [windowWidth]);

    const backgroundContentStyle = backgroundContent === THEME_CONTENT.gray ? styles.bgGray : styles.default;

    return (
        <div
            className={styles.container}
            style={{
                backgroundColor: `${backgroundColor ?? '#ffff'}`,
                background: `${backgroundLinearGradient}`,
                height: heightContainer,
                paddingBottom: isMobile ? paddingBottomContainerMobile : paddingBottomContainer,
                paddingTop: isMobile ? paddingTopContainerMobile : paddingTopContainer,
            }}
        >
            <div
                className={styles.backgroundImage}
                style={{
                    zIndex: `${backgroundImage?.zIndex}`,
                }}
            >
                {isMobile ? <img src={props.backgroundImage?.imageMobile || ''} alt="" /> : <img src={props.backgroundImage?.image || ''} alt="" />}
            </div>
            <div
                className={classNames(
                    styles.wrapperContent,
                    backgroundContentStyle,
                    isMobile
                        ? {
                              [styles.topMobile]: image?.positionImageMobile === IMAGE_POSITION_MOBILE.TopMobile,
                              [styles.bottomMobile]: image?.positionImageMobile === IMAGE_POSITION_MOBILE.BottomMobile,
                          }
                        : {
                              [styles.left]: image?.positionImage === IMAGE_POSITION.Left,
                              [styles.right]: image?.positionImage === IMAGE_POSITION.Right,
                              [styles.top]: image?.positionImage === IMAGE_POSITION.Top,
                              [styles.topRight]: image?.positionImage === IMAGE_POSITION.TopRight,
                              [styles.bottom]: image?.positionImage === IMAGE_POSITION.Bottom,
                              [styles.bottomRight]: image?.positionImage === IMAGE_POSITION.BottomRight,
                          }
                )}
            >
                <div className={styles.image} style={{ maxWidth: isMobile ? image?.maxWidthMobile : image?.maxWidth }}>
                    {isMobile ? <img src={image?.sourceMobile} alt="" /> : <img src={image?.source} alt="" />}

                    {isMobile && backgroundLoop && (
                        <div
                            className={styles.backgroundLoop}
                            style={
                                {
                                    ['--bottom-background-loop']: bottomBackgroundLoop,
                                } as React.CSSProperties
                            }
                        >
                            <img
                                src={backgroundLoop}
                                alt=""
                                className={classNames(styles.backgroundLoopImage, {
                                    [styles.top]: positionImageLoop === 'top',
                                    [styles.bottom]: positionImageLoop === 'bottom',
                                })}
                            />
                        </div>
                    )}
                </div>

                <div className={styles.wrapperText}>
                    <div className={styles.wrapper}>
                        <div
                            className={styles.title}
                            style={
                                {
                                    ['--fil-color']: text.fillColor,
                                    maxWidth: isMobile ? text.maxWidthTitleMobile : '100%',
                                } as React.CSSProperties
                            }
                            dangerouslySetInnerHTML={{ __html: text.title || '' }}
                        ></div>
                        <div
                            className={styles.description}
                            dangerouslySetInnerHTML={{ __html: text?.description || '' }}
                            style={{ maxWidth: text.maxWidthDescription } as React.CSSProperties}
                        ></div>
                        {text.ctaText && (
                            <div className={styles.button}>
                                <BusinessButton position={POSITION.LEFT} color={text.backgroundButton} link={text.ctaLink} buttonText={text.ctaText} autoMargin={false} target="_blank" />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BusinessHubImageBlock;
